import React, { useEffect, useState } from 'react';
import { useAdminTheme } from '../context/AdminThemeContext';
import { Search, Loader2, Calendar, Mail, Phone, BookOpen, Send, Filter, X, RefreshCw } from 'lucide-react';
import Notification from '../../Component/Notification';

const QueryList: React.FC = () => {
  const { colors } = useAdminTheme();
  
  const [queries, setQueries] = useState<any[]>([]);
  const [syllabusFiles, setSyllabusFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // --- FILTERS STATE ---
  const [searchText, setSearchText] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterCourse, setFilterCourse] = useState('');

  // --- SELECTION STATE ---
  // Stores selected file ID for each query row: { queryId: fileId }
  const [selectedFiles, setSelectedFiles] = useState<{ [key: number]: string }>({});
  const [sendingId, setSendingId] = useState<number | null>(null);

  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false, message: '', type: 'success'
  });

  // --- Fetch Data ---
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [qRes, sRes] = await Promise.all([
        // fetch('http://localhost:2025/api/admin/queries'),
        // fetch('http://localhost:2025/api/admin/syllabus')
        fetch('https://www.projectinfotech.in/api/admin/queries'),
        fetch('https://www.projectinfotech.in/api/admin/syllabus')
      ]);
      
      if (qRes.ok) {
          const data = await qRes.json();
          // Sort by latest
          setQueries(data.sort((a:any, b:any) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()));
      }
      if (sRes.ok) {
          setSyllabusFiles(await sRes.json());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- Filtering Logic ---
  const filteredQueries = queries.filter(q => {
    const matchesSearch = 
        q.name?.toLowerCase().includes(searchText.toLowerCase()) || 
        q.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        q.mobile?.includes(searchText);
    
    const matchesCourse = filterCourse ? q.course === filterCourse : true;
    
    // Date format match (YYYY-MM-DD)
    const matchesDate = filterDate ? q.submittedAt?.startsWith(filterDate) : true;

    return matchesSearch && matchesCourse && matchesDate;
  });

  // Get Unique Courses for Filter Dropdown
  const uniqueCourses = Array.from(new Set(queries.map(q => q.course)));

  // --- Handle Row Dropdown Change ---
  const handleSelectionChange = (queryId: number, fileId: string) => {
    setSelectedFiles(prev => ({ ...prev, [queryId]: fileId }));
  };

  // --- Send Logic ---
  const handleSend = async (query: any) => {
    const fileId = selectedFiles[query.id];

    if (!fileId) {
        setNotify({ isOpen: true, message: 'Please select a syllabus PDF first.', type: 'error' });
        return;
    }

    setSendingId(query.id);
    
    try {
        // const res = await fetch('http://localhost:2025/api/admin/queries/send-syllabus', {
        const res = await fetch('https://www.projectinfotech.in/api/admin/queries/send-syllabus', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                email: query.email,
                name: query.name,
                fileId: fileId
            })
        });

        if (res.ok) {
            setNotify({ isOpen: true, message: `Syllabus sent to ${query.name}!`, type: 'success' });
        } else {
            setNotify({ isOpen: true, message: 'Failed to send.', type: 'error' });
        }
    } catch (err) {
        setNotify({ isOpen: true, message: 'Server error.', type: 'error' });
    } finally {
        setSendingId(null);
    }
  };

  const resetFilters = () => {
      setSearchText('');
      setFilterCourse('');
      setFilterDate('');
  };

  return (
    <div className="animate-fade-in-up">
      <Notification isOpen={notify.isOpen} message={notify.message} type={notify.type} onClose={() => setNotify({ ...notify, isOpen: false })} />

      {/* --- HEADER --- */}
      <div className="flex justify-between items-end mb-6">
        <div>
            <h2 className={`text-2xl font-bold ${colors.navbarText}`}>Student Inquiries</h2>
            <p className="text-sm opacity-60">Filter and respond to leads</p>
        </div>
        <button onClick={fetchData} className={`p-2 rounded-full hover:bg-gray-100 transition text-gray-500`} title="Refresh Data">
            <RefreshCw size={20} />
        </button>
      </div>

      {/* --- FILTERS SECTION --- */}
      <div className={`p-4 rounded-xl border flex flex-wrap gap-4 items-center mb-6 shadow-sm ${colors.cardBg} ${colors.borderColor}`}>
            <div className="flex items-center gap-2 text-sm font-bold opacity-70 mr-2">
                <Filter size={16} /> Filters:
            </div>
            
            {/* Search Input */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input 
                    type="text" placeholder="Search Name, Email..." 
                    value={searchText} onChange={e => setSearchText(e.target.value)}
                    className={`pl-9 pr-3 py-2 rounded-lg text-sm border focus:outline-none w-48 transition-all focus:ring-2 focus:ring-red-500/20 ${colors.inputBg} ${colors.borderColor}`}
                />
            </div>

            {/* Course Filter Dropdown */}
            <select 
                value={filterCourse} onChange={e => setFilterCourse(e.target.value)}
                className={`px-3 py-2 rounded-lg text-sm border focus:outline-none w-48 cursor-pointer ${colors.inputBg} ${colors.borderColor}`}
            >
                <option value="">All Courses</option>
                {uniqueCourses.map((c: any) => <option key={c} value={c}>{c}</option>)}
            </select>

            {/* Date Filter */}
            <input 
                type="date" 
                value={filterDate} onChange={e => setFilterDate(e.target.value)}
                className={`px-3 py-2 rounded-lg text-sm border focus:outline-none cursor-pointer ${colors.inputBg} ${colors.borderColor}`}
            />

            {/* Reset Button */}
            {(searchText || filterCourse || filterDate) && (
                <button 
                    onClick={resetFilters}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-bold ml-auto px-3 py-1 rounded-md hover:bg-red-50 transition"
                >
                    <X size={14} /> Clear
                </button>
            )}
      </div>

      {/* --- DATA TABLE --- */}
      <div className={`rounded-xl shadow-sm border overflow-hidden ${colors.cardBg} ${colors.borderColor}`}>
        {loading ? (
           <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-red-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
                <thead className={`${colors.tableHeaderBg}`}>
                <tr>
                    <th className="p-4 font-semibold text-sm whitespace-nowrap">Date</th>
                    <th className="p-4 font-semibold text-sm">Student Details</th>
                    <th className="p-4 font-semibold text-sm">Inquired Course</th>
                    <th className="p-4 font-semibold text-sm w-64">Select Syllabus</th>
                    <th className="p-4 font-semibold text-sm text-right">Action</th>
                </tr>
                </thead>
                <tbody className={`${colors.cardText}`}>
                {filteredQueries.length > 0 ? (
                    filteredQueries.map((q) => (
                    <tr key={q.id} className={`border-t ${colors.borderColor} hover:opacity-80 transition-opacity`}>
                        {/* Date */}
                        <td className="p-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                                <Calendar size={14} className="opacity-60" />
                                {new Date(q.submittedAt).toLocaleDateString()}
                            </div>
                        </td>

                        {/* Student */}
                        <td className="p-4">
                            <div className="font-bold text-sm">{q.name}</div>
                            <div className="text-xs opacity-70 flex flex-col mt-1">
                                <span className="flex items-center gap-1"><Mail size={10}/> {q.email}</span>
                                <span className="flex items-center gap-1 mt-0.5"><Phone size={10}/> {q.mobile}</span>
                            </div>
                        </td>

                        {/* Course Tag */}
                        <td className="p-4">
                            <span className="px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 inline-flex items-center gap-1 border border-blue-200">
                                <BookOpen size={10} /> {q.course}
                            </span>
                        </td>
                        
                        {/* --- ROW DROPDOWN (Select Syllabus) --- */}
                        <td className="p-4">
                            <select 
                                value={selectedFiles[q.id] || ""}
                                onChange={(e) => handleSelectionChange(q.id, e.target.value)}
                                className={`w-full p-2 text-sm rounded-lg border focus:ring-2 focus:ring-red-500/20 outline-none cursor-pointer transition-shadow ${colors.inputBg} ${colors.borderColor}`}
                            >
                                <option value="">-- Select PDF --</option>
                                {syllabusFiles.map(file => (
                                    <option key={file.id} value={file.id}>
                                        {file.fileName}
                                    </option>
                                ))}
                            </select>
                        </td>

                        {/* --- SEND BUTTON --- */}
                        <td className="p-4 text-right">
                             <button
                                onClick={() => handleSend(q)}
                                disabled={sendingId === q.id}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all shadow-sm
                                    ${sendingId === q.id 
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                                        : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-md active:scale-95'
                                    }
                                `}
                             >
                                {sendingId === q.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                {sendingId === q.id ? 'Sending...' : 'Send'}
                             </button>
                        </td>
                    </tr>
                ))
                ) : (
                    <tr>
                        <td colSpan={5} className="p-10 text-center opacity-50">
                            No inquiries found matching your filters.
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryList;